import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  api,
  ActorRow,
  CommandLogRow,
  SpaceStatus as SpaceStatusT,
  SpaceSummary,
} from '../api';
import { StatusBadge } from '../components/StatusBadge';

// ── helpers ──────────────────────────────────────────────────

const ACTOR_TYPE_LABELS: Record<number, string> = {
  0: 'Generic', 1: 'Character', 2: 'Camera', 3: 'FX', 4: 'Light',
};

const STATUS_LABELS: Record<number, string> = {
  0: 'pending', 1: 'sent', 2: 'succeeded', 3: 'failed',
  4: 'timeout', 5: 'preempted', 6: 'missed_deadline', 7: 'cancelled',
};

const PIE_COLORS = [
  '#5fa8ff', '#60dbb5', '#f59e4b', '#f4728b', '#a78bfa',
  '#38bdf8', '#fb923c', '#4ade80', '#f87171', '#c084fc',
];

type Segment = { label: string; value: number; color: string };

// ── SVG Pie Chart ────────────────────────────────────────────

function PieChart({
  segments,
  size = 80,
}: {
  segments: Segment[];
  size?: number;
}): ReactElement {
  if (segments.length === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={size / 2 - 2} fill="#1e293b" />
      </svg>
    );
  }

  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 2;
  const total = segments.reduce((s, seg) => s + seg.value, 0);

  let cumulative = 0;
  const slices = segments.map((seg) => {
    const startAngle = total > 0 ? (cumulative / total) * 2 * Math.PI : 0;
    cumulative += seg.value;
    const endAngle = total > 0 ? (cumulative / total) * 2 * Math.PI : 0;
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

    const x1 = cx + r * Math.sin(startAngle);
    const y1 = cy - r * Math.cos(startAngle);
    const x2 = cx + r * Math.sin(endAngle);
    const y2 = cy - r * Math.cos(endAngle);

    const path =
      endAngle - startAngle >= 2 * Math.PI - 0.001
        ? `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r}`
        : `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;

    return { ...seg, path };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.map((s) => (
        <path key={s.label} d={s.path} fill={s.color} />
      ))}
      <circle cx={cx} cy={cy} r={r * 0.55} fill="#0f1729" />
    </svg>
  );
}

// ── Stat card with pie ───────────────────────────────────────

function PieCard({
  title,
  total,
  segments,
}: {
  title: string;
  total: number;
  segments: Segment[];
}): ReactElement {
  return (
    <div className="bg-panel rounded-lg p-4 flex flex-col gap-3">
      <div className="text-xs text-slate-400">{title}</div>
      <div className="flex items-center gap-4">
        <PieChart segments={segments} />
        <div className="flex flex-col gap-1 min-w-0">
          <div className="text-xl font-bold">{total}</div>
          <div className="text-[10px] text-slate-500 leading-tight">
            {segments.slice(0, 5).map((s) => (
              <div key={s.label} className="flex items-center gap-1">
                <span
                  className="inline-block w-2 h-2 rounded-sm shrink-0"
                  style={{ backgroundColor: s.color }}
                />
                <span className="truncate">
                  {s.label}: {s.value}
                </span>
              </div>
            ))}
            {segments.length > 5 && (
              <div className="text-slate-600">+{segments.length - 5} more</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page component ───────────────────────────────────────────

export function SpaceDashboard(): ReactElement {
  const params = useParams<{ id?: string }>();
  const [space, setSpace] = useState<SpaceSummary | null>(null);
  const [status, setStatus] = useState<SpaceStatusT | null>(null);
  const [log, setLog] = useState<CommandLogRow[]>([]);
  const [actors, setActors] = useState<ActorRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actorFilter, setActorFilter] = useState<string>('');
  const [commandFilter, setCommandFilter] = useState<string>('');
  const [limitKey, setLimitKey] = useState<string>('recent200');

  const limitValue = (): number => {
    if (limitKey === 'recent200') return 200;
    if (limitKey === 'recent1000') return 1000;
    return 10000;
  };

  useEffect(() => {
    if (!params.id) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const [s, st, cl, ac] = await Promise.all([
          api.getSpace(params.id!),
          api.getSpaceStatus(params.id!),
          api.listCommandLog(params.id!, limitValue()),
          api.listActors(params.id!).catch(() => [] as ActorRow[]),
        ]);
        if (!cancelled) {
          setSpace(s);
          setStatus(st);
          setLog(cl);
          setActors(ac);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'failed to load');
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [params.id, limitKey]);

  const commandTypes = useMemo(() => {
    const seen = new Set<string>();
    for (const row of log) {
      if (row.command) seen.add(row.command);
    }
    return Array.from(seen).sort();
  }, [log]);

  const filteredLog = useMemo(() => {
    return log.filter((row) => {
      if (actorFilter && row.actorId !== actorFilter) return false;
      if (commandFilter && row.command !== commandFilter) return false;
      return true;
    });
  }, [log, actorFilter, commandFilter]);

  // ── Pie chart data ───────────────────────────────────

  const actorTypeSegments = useMemo((): Segment[] => {
    const counts: Record<number, number> = {};
    for (const a of actors) {
      counts[a.actorType] = (counts[a.actorType] ?? 0) + 1;
    }
    return Object.entries(counts)
      .map(([type, n], i) => ({
        label: ACTOR_TYPE_LABELS[Number(type)] ?? `type ${type}`,
        value: n,
        color: PIE_COLORS[i % PIE_COLORS.length]!,
      }))
      .sort((a, b) => b.value - a.value);
  }, [actors]);

  const intentSegments = useMemo((): Segment[] => {
    const counts: Record<string, number> = {};
    for (const row of log) {
      const c = row.command || '(none)';
      counts[c] = (counts[c] ?? 0) + 1;
    }
    return Object.entries(counts)
      .map(([cmd, n], i) => ({
        label: cmd,
        value: n,
        color: PIE_COLORS[i % PIE_COLORS.length]!,
      }))
      .sort((a, b) => b.value - a.value);
  }, [log]);

  const statusSegments = useMemo((): Segment[] => {
    const counts: Record<number, number> = {};
    for (const row of log) {
      counts[row.status] = (counts[row.status] ?? 0) + 1;
    }
    return Object.entries(counts)
      .map(([s, n], i) => ({
        label: STATUS_LABELS[Number(s)] ?? `status ${s}`,
        value: n,
        color: PIE_COLORS[i % PIE_COLORS.length]!,
      }))
      .sort((a, b) => b.value - a.value);
  }, [log]);

  // ── Display helpers ──────────────────────────────────

  const actorName = (actorId: string | null): string => {
    if (!actorId) return '—';
    return actors.find((a) => a.id === actorId)?.name ?? actorId.slice(0, 8);
  };

  const statusLabel = (s: number): string =>
    STATUS_LABELS[s] ?? String(s);

  if (!params.id) return <p>Missing space id</p>;
  if (error) return <p className="text-rose-400">{error}</p>;
  if (!space || !status) return <p className="text-slate-400">Loading…</p>;

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">{space.title}</h1>
        <StatusBadge status={status.status} />
      </div>

      {/* ── Pie stat cards ─────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PieCard
          title={`Actors · ${actors.length} total`}
          total={actors.length}
          segments={actorTypeSegments}
        />
        <PieCard
          title={`Commands · ${log.length} total`}
          total={log.length}
          segments={intentSegments}
        />
        <PieCard
          title={`Command Status`}
          total={log.length}
          segments={statusSegments}
        />
      </div>

      {/* ── Command log ────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-slate-300">
            Commands{' '}
            <span className="font-normal text-slate-500">
              ({filteredLog.length})
            </span>
          </h2>
          <div className="flex gap-2">
            <select
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-300"
            >
              <option value="">All Actors</option>
              {actors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <select
              value={commandFilter}
              onChange={(e) => setCommandFilter(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-300"
            >
              <option value="">All Commands</option>
              {commandTypes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              value={limitKey}
              onChange={(e) => setLimitKey(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-300"
            >
              <option value="all">All Commands</option>
              <option value="recent200">Recent 200</option>
              <option value="recent1000">Recent 1000</option>
            </select>
          </div>
        </div>
        {filteredLog.length === 0 ? (
          <p className="text-slate-400 text-sm">No commands match the filters.</p>
        ) : (
          <ul className="space-y-1 text-xs font-mono">
            {filteredLog.map((row) => (
              <li
                key={row.id}
                className="bg-panel rounded p-2 flex items-center justify-between gap-4"
              >
                <span className="text-slate-400 shrink-0 w-36 truncate">
                  {actorName(row.actorId)}
                </span>
                <span className="text-accent shrink-0 w-32">{row.command}</span>
                <span
                  className={`shrink-0 w-20 ${
                    row.status === 2
                      ? 'text-emerald-400'
                      : row.status === 3 || row.status === 4
                        ? 'text-rose-400'
                        : 'text-slate-400'
                  }`}
                >
                  {statusLabel(row.status)}
                </span>
                <span className="text-slate-500 shrink-0 w-44 text-right">
                  {new Date(row.createdAt).toLocaleString()}
                </span>
                {row.error && (
                  <span className="text-rose-400 truncate min-w-0">
                    {row.error}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
